import React, { useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { useGetStockEditRequestsQuery, appApi } from "../../../../store/appApi";
import { selectUserRole } from "../../../../store/authSlice";
import { reviewEditRequestApi } from "../../../../services/inventory";
import PageTitle from "../../../layouts/PageTitle";

const ADMIN_ROLES = ["super-admin", "sub-admin"];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const STATUS_BADGE = {
  pending:  "badge-warning",
  approved: "badge-success",
  rejected: "badge-danger",
};

const EditRequests = () => {
  const dispatch  = useDispatch();
  const userRole  = useSelector(selectUserRole);
  const isAdmin   = ADMIN_ROLES.includes(userRole);

  const [statusFilter, setStatusFilter] = useState("pending");
  const [reviewRow,    setReviewRow]    = useState(null);
  const [reviewNote,   setReviewNote]   = useState("");
  const [reviewing,    setReviewing]    = useState(false);

  const { data, isLoading, isFetching } = useGetStockEditRequestsQuery(statusFilter);
  const requests = Array.isArray(data?.requests) ? data.requests : [];
  const loading  = isLoading || isFetching;

  const openReview  = (r) => { setReviewRow(r); setReviewNote(""); };
  const closeReview = () => { setReviewRow(null); setReviewNote(""); };

  const handleReview = async (action) => {
    try {
      setReviewing(true);
      await reviewEditRequestApi(reviewRow._id, { action, reviewNote });
      dispatch(appApi.util.invalidateTags(["StockEditRequests", "StoreTransactions"]));
      closeReview();
      Swal.fire({
        icon: action === "approved" ? "success" : "info",
        title: action === "approved" ? "Approved & Applied" : "Rejected",
        text: action === "approved"
          ? "Stock entry has been updated and balances recalculated."
          : "Edit request has been rejected.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.response?.data?.message || "Action failed." });
    } finally {
      setReviewing(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="card">
        <div className="card-body text-center py-5 text-muted">
          <i className="fa fa-lock fa-2x mb-3 d-block" />
          Access restricted to admins.
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Edit Requests" motherMenu="Inventory" />

      <div className="card">
        <div className="card-header d-flex align-items-center gap-3 flex-wrap">
          <h5 className="card-title mb-0">
            <i className="fa fa-edit me-2 text-warning" />
            Stock Edit Requests
          </h5>
          <div className="ms-auto d-flex gap-2">
            {["pending", "approved", "rejected", ""].map((s) => (
              <button
                key={s}
                className={`btn btn-sm ${statusFilter === s ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="display dataTable no-footer w-100">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Requested By</th>
                  <th>Item</th>
                  <th>Entry Type</th>
                  <th>Entry Date</th>
                  <th>Current Qty</th>
                  <th>Requested Qty</th>
                  <th>Reason</th>
                  <th>Requested At</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={11} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" />Loading…
                  </td></tr>
                )}
                {!loading && requests.length === 0 && (
                  <tr><td colSpan={11} className="text-center py-5 text-muted">
                    {statusFilter === "pending" ? "No pending edit requests." : "No requests found."}
                  </td></tr>
                )}
                {!loading && requests.map((r, idx) => {
                  const stock = r.stockId;
                  return (
                    <tr key={r._id}>
                      <td className="text-muted small">{idx + 1}</td>
                      <td className="small">
                        <div className="fw-semibold">{r.requestedBy?.name || "—"}</div>
                        <div className="text-muted" style={{ fontSize: "0.7rem" }}>{r.requestedBy?.role}</div>
                      </td>
                      <td className="small fw-semibold">
                        {stock?.skuId?.skuName || "—"}
                        {stock?.skuId?.measurement && (
                          <span className="text-muted ms-1">({stock.skuId.measurement})</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${stock?.entryType === "add" ? "badge-success" : "badge-warning"}`}>
                          {stock?.entryType === "add" ? "Received" : "Issued"}
                        </span>
                      </td>
                      <td className="small">{fmtDate(stock?.created)}</td>
                      <td className="small fw-semibold">{stock?.qty ?? "—"}</td>
                      <td className="small fw-semibold text-primary">{r.newQty}</td>
                      <td className="small" style={{ maxWidth: 180 }}>
                        <span title={r.reason}>{r.reason?.length > 60 ? r.reason.slice(0, 60) + "…" : r.reason}</span>
                      </td>
                      <td className="small text-muted">{fmtDateTime(r.createdAt)}</td>
                      <td>
                        <span className={`badge ${STATUS_BADGE[r.status]}`}>
                          {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                        </span>
                        {r.status !== "pending" && r.reviewedBy && (
                          <div className="small text-muted mt-1" style={{ fontSize: "0.65rem" }}>
                            by {r.reviewedBy.name}
                          </div>
                        )}
                      </td>
                      <td>
                        {r.status === "pending" ? (
                          <button
                            className="btn btn-xs btn-primary"
                            onClick={() => openReview(r)}
                          >
                            Review
                          </button>
                        ) : (
                          <span className="small text-muted">
                            {fmtDate(r.reviewedAt)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Review modal ──────────────────────────────────────────────────── */}
      {reviewRow && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa fa-check-circle me-2 text-primary" />
                  Review Edit Request
                </h5>
                <button className="btn-close" onClick={closeReview} />
              </div>
              <div className="modal-body">
                <div className="card mb-3 bg-light border-0">
                  <div className="card-body py-2">
                    <div className="row g-2 small">
                      <div className="col-6">
                        <span className="text-muted">Item:</span>{" "}
                        <strong>{reviewRow.stockId?.skuId?.skuName}</strong>
                      </div>
                      <div className="col-6">
                        <span className="text-muted">Entry Date:</span>{" "}
                        <strong>{fmtDate(reviewRow.stockId?.created)}</strong>
                      </div>
                      <div className="col-6">
                        <span className="text-muted">Current Qty:</span>{" "}
                        <strong>{reviewRow.stockId?.qty}</strong>
                      </div>
                      <div className="col-6">
                        <span className="text-muted">Requested Qty:</span>{" "}
                        <strong className="text-primary">{reviewRow.newQty}</strong>
                      </div>
                      {reviewRow.newPrice != null && (
                        <div className="col-6">
                          <span className="text-muted">New Price:</span>{" "}
                          <strong>₹{reviewRow.newPrice}</strong>
                        </div>
                      )}
                      <div className="col-12">
                        <span className="text-muted">Requested by:</span>{" "}
                        <strong>{reviewRow.requestedBy?.name}</strong>{" "}
                        <span className="text-muted">({reviewRow.requestedBy?.role})</span>
                      </div>
                      <div className="col-12">
                        <span className="text-muted">Reason:</span>{" "}
                        <em>{reviewRow.reason}</em>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Review Note (optional)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Add a note for the employee…"
                  />
                </div>
              </div>
              <div className="modal-footer gap-2">
                <button type="button" className="btn btn-secondary" onClick={closeReview}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={reviewing}
                  onClick={() => handleReview("rejected")}
                >
                  {reviewing ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fa fa-times me-1" />}
                  Reject
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={reviewing}
                  onClick={() => handleReview("approved")}
                >
                  {reviewing ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fa fa-check me-1" />}
                  Approve & Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditRequests;
